/**
 * commentsModel.ts
 *
 * Pure types and helpers for the Comments_Panel: Anchors, the comment stream
 * and time formatting. No React and no storage.
 *
 * Requirements: 12.2, 12.3.
 */

import { JobType } from '../../graphql/types';
import { MODE_LABELS, isModeLocked, type LockedModes } from '../modes';
import {
  isValidMode,
  type SelectionParamKey,
  type WorkspaceMode,
  type WorkspaceParamsUpdate,
} from '../urlState';

/** What a comment is attached to (design.md, "Anchor"). */
export type AnchorType = 'mode' | 'field' | 'finding' | 'exception';

export interface CommentAnchor {
  type: AnchorType;
  /** Mode id, field id, finding id or exception id. */
  id: string;
  /** Text shown on the Anchor chip and in "Anchored to: …". */
  label: string;
  /**
   * Loan and document that were selected when the Anchor was made. Optional;
   * used to restore that context when navigating to a field or exception.
   */
  loan?: string;
  doc?: string;
}

/** A comment written by a user. */
export interface UserComment {
  kind: 'comment';
  id: string;
  author: string;
  /** ISO 8601 timestamp. */
  createdAt: string;
  anchor: CommentAnchor;
  text: string;
}

/** A system event shown in the same stream, for example "Analysis re-run". */
export interface SystemEvent {
  kind: 'event';
  id: string;
  /** ISO 8601 timestamp. */
  createdAt: string;
  text: string;
  anchor?: CommentAnchor;
}

export type CommentStreamEntry = UserComment | SystemEvent;

export type CommentSelection = Partial<Record<SelectionParamKey, string>>;

/** Anchor for a Mode: used when nothing is selected. */
export function modeAnchor(mode: WorkspaceMode): CommentAnchor {
  return { type: 'mode', id: mode, label: `${MODE_LABELS[mode]} mode` };
}

/**
 * Anchor for a new comment: the selected exception, else the selected field,
 * else the current Mode. A selected loan or document alone is context, not an
 * Anchor, so it falls through to the Mode (Requirement 12.3).
 */
export function resolveAnchor(
  selection: CommentSelection | undefined,
  mode: WorkspaceMode
): CommentAnchor {
  const context = {
    ...(selection?.loan ? { loan: selection.loan } : {}),
    ...(selection?.doc ? { doc: selection.doc } : {}),
  };
  if (selection?.exception) {
    return {
      type: 'exception',
      id: selection.exception,
      label: `Exception ${selection.exception}`,
      ...context,
    };
  }
  if (selection?.field) {
    return { type: 'field', id: selection.field, label: `Field ${selection.field}`, ...context };
  }
  return modeAnchor(mode);
}

/** Mode that shows fields, findings and exceptions, per Feature. */
const FIELD_MODE: Readonly<Record<JobType, WorkspaceMode>> = {
  [JobType.DD]: 'extract',
  [JobType.BA]: 'extract',
  [JobType.TR]: 'review',
};
const FINDING_MODE: Readonly<Record<JobType, WorkspaceMode>> = {
  [JobType.DD]: 'analyze',
  [JobType.BA]: 'analyze',
  [JobType.TR]: 'findings',
};
const EXCEPTION_MODE: Partial<Record<JobType, WorkspaceMode>> = {
  [JobType.TR]: 'analysis',
};

/** Result of resolving an Anchor to Workspace URL params. */
export type AnchorTarget =
  | { ok: true; update: WorkspaceParamsUpdate }
  | { ok: false; reason: 'locked' | 'unavailable'; message: string };

/**
 * Where selecting an Anchor takes the user, as a Workspace URL update.
 *
 * - mode: switch to that Mode; the selection is left alone.
 * - field: the Feature's field Mode, with `field` (and the Anchor's `loan` and
 *   `doc` when known). Any selected exception is cleared so the new selection
 *   resolves to this field.
 * - exception: Title analysis with `exception` (and `loan`/`doc` when known).
 * - finding: the Feature's findings Mode. The URL has no finding key yet, so
 *   nothing else changes.
 *
 * Never targets a Mode that is invalid for the Feature or locked; the result
 * then explains why in plain language.
 */
export function anchorToWorkspaceTarget(
  anchor: CommentAnchor,
  feature: JobType,
  locked?: LockedModes
): AnchorTarget {
  let mode: WorkspaceMode | undefined;
  let selection: WorkspaceParamsUpdate = {};

  switch (anchor.type) {
    case 'mode':
      mode = isValidMode(feature, anchor.id) ? anchor.id : undefined;
      break;
    case 'field':
      mode = FIELD_MODE[feature];
      selection = { field: anchor.id, exception: null };
      break;
    case 'exception':
      mode = EXCEPTION_MODE[feature];
      selection = { exception: anchor.id };
      break;
    case 'finding':
      mode = FINDING_MODE[feature];
      break;
  }
  if (anchor.type === 'field' || anchor.type === 'exception') {
    if (anchor.loan) selection = { ...selection, loan: anchor.loan };
    if (anchor.doc) selection = { ...selection, doc: anchor.doc };
  }

  if (!mode || !isValidMode(feature, mode)) {
    return {
      ok: false,
      reason: 'unavailable',
      message: `${anchor.label} is not available in this view.`,
    };
  }
  if (isModeLocked(locked, mode)) {
    const reason = locked?.[mode];
    const subject =
      anchor.type === 'mode'
        ? `${MODE_LABELS[mode]} mode is locked`
        : `${anchor.label} is in ${MODE_LABELS[mode]} mode, which is locked`;
    return {
      ok: false,
      reason: 'locked',
      message: `${subject}${reason ? `: ${reason}` : '.'}`,
    };
  }
  return { ok: true, update: { mode, ...selection } };
}

/** Oldest first; entries with equal timestamps keep their original order. */
export function sortStream<T extends CommentStreamEntry>(entries: readonly T[]): T[] {
  return entries
    .map((entry, index) => ({ entry, index, time: Date.parse(entry.createdAt) }))
    .sort((a, b) => {
      const ta = Number.isNaN(a.time) ? 0 : a.time;
      const tb = Number.isNaN(b.time) ? 0 : b.time;
      return ta - tb || a.index - b.index;
    })
    .map(({ entry }) => entry);
}

/** Number of user comments. System events are not counted. */
export function countComments(entries: readonly CommentStreamEntry[]): number {
  return entries.filter((entry) => entry.kind === 'comment').length;
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** Short relative time such as "just now", "5 min ago", "3 h ago", "Jan 4". */
export function formatCommentTime(iso: string, now: Date = new Date()): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return '';
  const elapsed = now.getTime() - then;
  if (elapsed < MINUTE) return 'just now';
  if (elapsed < HOUR) return `${Math.floor(elapsed / MINUTE)} min ago`;
  if (elapsed < DAY) return `${Math.floor(elapsed / HOUR)} h ago`;
  return new Date(then).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** Trimmed text, or null when there is nothing to post. */
export function normalizeCommentText(text: string): string | null {
  const trimmed = text.trim();
  return trimmed ? trimmed : null;
}
